/** A KPI is open for entry everywhere it can be entered. */
export const OPEN = "OPEN";
/** A KPI is closed at every location in scope — entry is blocked outright. */
export const LOCKED = "LOCKED";
/** A KPI is closed at some locations but still open at others. */
export const PARTIAL = "PARTIAL";

/** What a KPI with no deadline configured looks like. */
const NO_LOCK = {
  status: OPEN,
  dueDate: null,
  lockedLocationIds: [],
  extendedUntil: null,
};

/**
 * Summarises the server's per-location lock rows into one state per KPI.
 *
 * A KPI can be past its deadline at one location and still open at another, so
 * collapsing this to a single boolean would either block entry that should be
 * allowed or hide a lock that will refuse the save. The three states keep that
 * distinction: only {@link LOCKED} disables entry outright.
 * @param {object[]} rows the lock-state rows from the API.
 * @returns {Map<number, object>} KPI id to its summarised lock state.
 */
export function summariseLocks(rows) {
  const byKpi = new Map();

  for (const row of rows ?? []) {
    const kpiId = Number(row.questionId);
    const entry = byKpi.get(kpiId) ?? {
      dueDate: row.dueDate,
      lockedLocationIds: [],
      openCount: 0,
      extendedUntil: null,
    };

    if (row.isLocked) entry.lockedLocationIds.push(row.locationId);
    else entry.openCount += 1;

    if (row.extendedUntil && !entry.extendedUntil) {
      entry.extendedUntil = row.extendedUntil;
    }
    byKpi.set(kpiId, entry);
  }

  const summarised = new Map();
  for (const [kpiId, entry] of byKpi) {
    const lockedCount = entry.lockedLocationIds.length;
    summarised.set(kpiId, {
      status:
        lockedCount === 0 ? OPEN : entry.openCount === 0 ? LOCKED : PARTIAL,
      dueDate: entry.dueDate,
      lockedLocationIds: entry.lockedLocationIds,
      extendedUntil: entry.extendedUntil,
    });
  }
  return summarised;
}

/**
 * Reads one KPI's lock state, defaulting to open.
 * @param {Map<number, object>} locks the summarised locks.
 * @param {number|string} kpiId the KPI to look up.
 * @returns {object} the KPI's lock state.
 */
export function lockFor(locks, kpiId) {
  return locks?.get(Number(kpiId)) ?? NO_LOCK;
}

/**
 * Whether an access request already covers a KPI at a location.
 *
 * Status is compared case-insensitively so a change in how the API serialises
 * the enum cannot silently stop matching.
 * @param {object[]} requests the raised access requests.
 * @param {number|string} kpiId the KPI.
 * @param {number|null} locationId the location, or null for qualitative.
 * @returns {boolean} whether a request is already pending.
 */
export function hasPendingRequest(requests, kpiId, locationId) {
  return (requests ?? []).some(
    (req) =>
      Number(req.questionId) === Number(kpiId) &&
      (locationId == null || Number(req.locationId) === Number(locationId)) &&
      String(req.status).toLowerCase() === "pending",
  );
}

/**
 * The still-undecided requests raised against a KPI.
 *
 * An administrator approves or rejects these in place on the KPI row, so the
 * row needs their ids rather than just whether any exist.
 * @param {object[]} requests the raised access requests.
 * @param {number|string} kpiId the KPI.
 * @returns {number[]} the pending request ids.
 */
export function pendingRequestIds(requests, kpiId) {
  return (requests ?? [])
    .filter(
      (req) =>
        Number(req.questionId) === Number(kpiId) &&
        String(req.status).toLowerCase() === "pending",
    )
    .map((req) => Number(req.id));
}

/**
 * The locations a KPI's pending requests belong to.
 *
 * Sent with an approve or reject so the server can scope the decision to the
 * decider's own sites.
 * @param {object[]} requests the raised access requests.
 * @param {number|string} kpiId the KPI.
 * @returns {number[]} the location ids, excluding qualitative rows with none.
 */
export function pendingRequestLocationIds(requests, kpiId) {
  return [
    ...new Set(
      (requests ?? [])
        .filter(
          (req) =>
            Number(req.questionId) === Number(kpiId) &&
            String(req.status).toLowerCase() === "pending" &&
            req.sourceId != null,
        )
        .map((req) => Number(req.sourceId)),
    ),
  ];
}

/**
 * Whether the last word on a KPI's access was a rejection.
 *
 * A rejected request stops being pending, so without this the row falls back to
 * offering "Request for Access" again — indistinguishable to the requester from
 * never having asked. They would re-request, be rejected again, and never learn
 * why. Reading the decision closes that loop.
 *
 * A pending request outranks any earlier decision: once the KPI has been asked
 * about again, the outstanding question is what the row should show. Decisions
 * are ordered by when they were made, falling back to id where an approval date
 * is missing.
 * @param {object[]} requests the raised access requests.
 * @param {number|string} kpiId the KPI.
 * @param {number|null} locationId the location, or null for qualitative.
 * @returns {boolean} whether the most recent decision was a rejection.
 */
export function wasAccessDeclined(requests, kpiId, locationId) {
  const mine = (requests ?? []).filter(
    (req) =>
      Number(req.questionId) === Number(kpiId) &&
      (locationId == null || Number(req.sourceId) === Number(locationId)),
  );
  if (mine.some((req) => String(req.status).toLowerCase() === "pending")) {
    return false;
  }

  const decidedAt = (req) =>
    req.approvalDate ? Date.parse(req.approvalDate) : Number(req.id);
  const latest = mine.reduce(
    (newest, req) => (!newest || decidedAt(req) >= decidedAt(newest) ? req : newest),
    null,
  );
  return String(latest?.status).toLowerCase() === "rejected";
}

/**
 * Builds the request items for reopening a KPI at its locked locations.
 *
 * The financial year, period and reason are sent once at the payload level, so
 * these carry only what differs per row.
 * @param {object} params the KPI and its lock state.
 * @returns {object[]} one item per locked location.
 */
export function buildAccessRequests({ kpiId, lock }) {
  const locations = lock.lockedLocationIds.length
    ? lock.lockedLocationIds
    : [null];
  return locations.map((locationId) => ({
    questionId: Number(kpiId),
    ...(locationId == null ? {} : { locationId: Number(locationId) }),
  }));
}
